import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { PythonCodeEditor } from "@/components/ui/PythonCodeEditor";
import { useCreatePricingFunction, useUpdatePricingFunction } from "../hooks/useTenders";
import type { PricingPresetFunction } from "../types";

const DEFAULT_PARAM_NAME = "argument";
const MAX_FN_PARAMS = 10;

export function functionSignature(names: string[]): string {
  const list = names.length ? names : [DEFAULT_PARAM_NAME];
  return `def funkcja_presetu(${list.map((n) => `${n}=0`).join(", ")}):`;
}

// Replace the first line (the generated signature) of `code`, preserving the body.
function withSignature(code: string, names: string[]): string {
  const sig = functionSignature(names);
  const nl = code.indexOf("\n");
  if (nl === -1) return `${sig}\n    return ${names[0] ?? DEFAULT_PARAM_NAME}`;
  return sig + code.slice(nl);
}

function defaultFunctionCode(names: string[]): string {
  const list = names.length ? names : [DEFAULT_PARAM_NAME];
  return `${functionSignature(list)}\n    return ${list[0]}`;
}

// Best-effort extraction of a human message from an API error (envelope or DRF field errors).
export function extractApiError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  const pick = (v: unknown): string | null => {
    if (!v) return null;
    if (typeof v === "string") return v;
    if (Array.isArray(v)) return v.length ? pick(v[0]) : null;
    if (typeof v === "object") return pick(Object.values(v as Record<string, unknown>)[0]);
    return null;
  };
  const d = data as { errors?: unknown; code?: unknown; detail?: unknown } | undefined;
  return pick(d?.errors) ?? pick(d?.code) ?? pick(d?.detail) ?? fallback;
}

interface PresetFunctionModalProps {
  open: boolean;
  onClose: () => void;
  editing: PricingPresetFunction | null;
}

/** Create/edit a preset function: choose the number of arguments, rename them (the
 *  signature regenerates dynamically), and write the body. Shared by the pricing catalog
 *  tab and the dev "Funkcje wycen" page. */
export function PresetFunctionModal({ open, onClose, editing }: PresetFunctionModalProps) {
  const initialNames =
    editing?.param_names?.length ? editing.param_names : [DEFAULT_PARAM_NAME];
  const [name, setName] = useState(editing?.name ?? "");
  const [paramNames, setParamNames] = useState<string[]>(initialNames);
  const [code, setCode] = useState(editing?.code ?? defaultFunctionCode(initialNames));
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreatePricingFunction();
  const updateMutation = useUpdatePricingFunction(editing?.id ?? "");
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Changing count/names regenerates the signature line; the body is preserved.
  const applyNames = (next: string[]) => {
    setParamNames(next);
    setCode((c) => withSignature(c, next));
  };

  const setCount = (count: number) => {
    const n = Math.max(1, Math.min(MAX_FN_PARAMS, count || 1));
    const next = Array.from({ length: n }, (_, i) =>
      paramNames[i] ?? `${DEFAULT_PARAM_NAME}${i + 1}`
    );
    applyNames(next);
  };

  const renameParam = (i: number, value: string) => {
    const next = paramNames.map((p, idx) => (idx === i ? value : p));
    applyNames(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const data = { name: name.trim(), code, param_names: paramNames.map((p) => p.trim()) };
    try {
      if (editing) await updateMutation.mutateAsync(data);
      else await createMutation.mutateAsync(data);
      onClose();
    } catch (err) {
      setError(extractApiError(err, "Nie udało się zapisać funkcji. Sprawdź kod."));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? "Edytuj funkcję" : "Nowa funkcja pozycji presetu"}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nazwa <span className="text-red-500">*</span>
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="np. Sufit cenowy działek końcowych"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          />
        </div>

        {/* Arguments: count + renamable names — drives the signature dynamically. */}
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Liczba argumentów
            </label>
            <input
              type="number"
              min={1}
              max={MAX_FN_PARAMS}
              value={paramNames.length}
              onChange={(e) => setCount(parseInt(e.target.value, 10))}
              className="w-16 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {paramNames.map((p, i) => (
              <div key={i} className="flex items-center gap-1">
                <span className="text-[11px] text-gray-400">{i + 1}.</span>
                <input
                  value={p}
                  onChange={(e) => renameParam(i, e.target.value)}
                  spellCheck={false}
                  className="w-32 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Kod funkcji (Python)
          </label>
          <PythonCodeEditor value={code} onChange={setCode} rows={8} ariaLabel="Kod funkcji" />
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1 leading-relaxed">
            Nagłówek <code className="font-mono">def funkcja_presetu(…)</code> dostosowuje się do
            liczby i nazw argumentów. Uruchamiana jako{" "}
            <code className="font-mono">max(0, funkcja_presetu(…))</code>. Dozwolone: liczby,
            operatory, <code className="font-mono">if</code>, funkcje{" "}
            <code className="font-mono">math.*</code> i bezpieczne wbudowane (min, max, abs, round,
            int, float, pow, sum). Bez importów, pętli i dostępu do innych nazw. Argumenty
            niepodpięte do parametru presetu wchodzą jako <code className="font-mono">0</code>, więc
            sumę zmiennej liczby wartości zapiszesz jako{" "}
            <code className="font-mono">sum([a, b, c])</code>.
          </p>
        </div>
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400 whitespace-pre-line">{error}</p>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="submit" size="sm" loading={isPending} disabled={!name.trim()}>
            {editing ? "Zapisz zmiany" : "Dodaj funkcję"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
